<script lang="ts" setup>
import { navbarData, lp1Data, seoData } from '~/data'
import { Form, Field, ErrorMessage, defineRule } from 'vee-validate'
import type { ApiResponse } from '@/types/apiResponse'

useHead({
  title: 'Home',
  meta: [
    {
      name: 'description',
      content:
        'Se você quer ser um desenvolvedor de software diferenciado, que se destaca no mercado e que cresce rapidamente na carreira, proporcionando mais conforto e segurança para a sua vida e da sua família. Faça parte do Movimento Codernista agora.',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: `${seoData.mySite}`,
    },
  ],
})

// Generate OG Image
const siteData = useSiteConfig()
defineOgImage({
  props: {
    title: navbarData.homeTitle,
    description:
      'Seja um desenvolvedor de software diferenciado, que se destaca no mercado e que cresce rapidamente na carreira',
    siteName: siteData.url,
  },
})

// // Generate OG Image
// defineOgImageComponent('Home', {
//   headline: 'Greetings 👋',
//   title: navbarData.homeTitle,
//   description: 'Dive into web development with me and learn Js, Ts, Vue, Nuxt, Docker, k8s',
//   link: '/riyad.jpg',
// })

defineRouteRules({
  sitemap: {
    changefreq: 'monthly',
    priority: 0.3,
    lastmod: '2025-04-12',
  },
})

defineRule('required', (value: string) => {
  if (!value || !value.length) {
    return 'Campo obrigatório!'
  }
  return true
})

defineRule('name', (value: string) => {
  // Field is empty, should pass
  if (!value || !value.length) {
    return true
  }

  if (value.length < 3 || value.length > 100 || !/^[\w]{3,}([\s\w]{3,})*/.test(value)) {
    return 'Informe um nome válido!'
  }
  return true
})

defineRule('email', (value: string) => {
  // Field is empty, should pass
  if (!value || !value.length) {
    return true
  }
  // Check if email
  if (
    value.length < 8 ||
    value.length > 320 ||
    !/[\w0-9._%+-]+@[\w0-9.-]+\.[\w]{2,4}(\.[\w]{2})*/.test(value)
  ) {
    return 'Informe um e-mail válido!'
  }
  return true
})

defineRule('phone', (value: string) => {
  // Field is empty, should pass
  if (!value || !value.length) {
    return true
  }
  // Check if phone
  if (value.length != 14 || !/\([\d]{2}\)[\d]{5}-[\d]{4}/.test(value)) {
    return 'Informe um celular válido! Ex.: (11)99999-9999.'
  }
  return true
})
</script>

<template>
  <div class="container md:max-w-2xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl mx-auto text-zinc-600">
    <div class="grid grid-cols-1 py-2 sm:py-10 h-auto bg-[#FFD13C]">
      <div class="px-10 sm:px-20 lg:px-40 text-center">
        <h1
          class="text-black dark:text-zinc-300 font-semibold leading-tight text-3xl sm:text-4xl md:text-5xl my-5"
        >
          {{ lp1Data.title }}
        </h1>
        <p class="text-zinc-800 text-xl sm:text-2xl dark:text-zinc-300">
          {{ lp1Data.description }}
        </p>

        <div class="py-14">
          <a
            href="#lista-de-espera"
            class="bg-[#F4384E] hover:bg-[#bb2738] text-white text-md font-bold p-8 rounded shadow-lg"
            >Quero fazer parte do Movimento!</a
          >
        </div>
      </div>
    </div>
    <div class="grid grid-cols-1 py-5">
      <div class="px-10 sm:px-56 text-center">
        <h2
          class="text-black dark:text-zinc-300 font-semibold leading-tight text-2xl sm:text-3xl md:text-4xl my-5"
        >
          O que você vai conquistar fazendo parte do Movimento?
        </h2>
      </div>
    </div>
    <div
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 py-2 sm:py-5 px-10 items-center"
    >
      <div class="w-full h-72 rounded overflow-hidden shadow-lg">
        <NuxtImg
          format="webp"
          quality="70"
          width="389px"
          height="140px"
          class="w-full"
          src="/tecnologias-essenciais.jpg"
          alt="Technologias essenciais"
        />
        <div class="px-6 py-4">
          <h3 class="font-bold text-xl mb-2">Domínio de Tecnologias Essenciais</h3>
          <p class="text-gray-700 text-base">
            Aprofunde seus conhecimentos nas ferramentas e linguagens mais demandadas do mercado.
          </p>
        </div>
      </div>
      <div class="w-full h-72 rounded overflow-hidden shadow-lg">
        <NuxtImg
          format="webp"
          quality="70"
          width="389px"
          height="140px"
          class="w-full"
          src="/crescimento-acelerado.jpg"
          alt="Estratégias de Crescimento Acelerado"
        />
        <div class="px-6 py-4">
          <h3 class="font-bold text-xl mb-2">Estratégias de Crescimento Acelerado</h3>
          <p class="text-gray-700 text-base">
            Descubra os caminhos mais rápidos e eficazes para evoluir profissionalmente.
          </p>
        </div>
      </div>
      <div class="w-full h-72 rounded overflow-hidden shadow-lg">
        <NuxtImg
          format="webp"
          quality="70"
          width="389px"
          height="140px"
          class="w-full"
          src="/networking-estrategico.jpg"
          alt="Networking Estratégico"
        />
        <div class="px-6 py-4">
          <h3 class="font-bold text-xl mb-2">Networking Estratégico</h3>
          <p class="text-gray-700 text-base">
            Aprenda a construir conexões valiosas com outros profissionais da área.
          </p>
        </div>
      </div>
      <div class="w-full h-72 rounded overflow-hidden shadow-lg">
        <NuxtImg
          format="webp"
          quality="70"
          width="389px"
          height="140px"
          class="w-full"
          src="/destaque-portfolio.jpg"
          alt="Projetos de Destaque no Portfólio"
        />
        <div class="px-6 py-4">
          <h3 class="font-bold text-xl mb-2">Projetos de Destaque no Portfólio</h3>
          <p class="text-gray-700 text-base">
            Desenvolva projetos que realmente impressionam e abrem portas para novas oportunidades.
          </p>
        </div>
      </div>
      <div class="w-full h-72 rounded overflow-hidden shadow-lg">
        <NuxtImg
          format="webp"
          quality="70"
          width="389px"
          height="140px"
          class="w-full"
          src="/habilidades-lideranca-comunicacao.jpg"
          alt="Habilidades de Liderança e Comunicação"
        />
        <div class="px-6 py-4">
          <h3 class="font-bold text-xl mb-2">Habilidades de Liderança e Comunicação</h3>
          <p class="text-gray-700 text-base">
            Aprimore suas soft skills para se destacar como um líder técnico influente.
          </p>
        </div>
      </div>
      <div class="w-full h-72 rounded overflow-hidden shadow-lg">
        <NuxtImg
          format="webp"
          quality="70"
          width="389px"
          height="140px"
          class="w-full"
          src="/salarios-mais-atrativos.jpg"
          alt="Salários Mais Atrativos"
        />
        <div class="px-6 py-4">
          <h3 class="font-bold text-xl mb-2">Salários Mais Atrativos</h3>
          <p class="text-gray-700 text-base">
            Posicione-se para conquistar as melhores remunerações do mercado de TI.
          </p>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-1 my-10 py-10 h-auto bg-[#0891b2]">
      <div class="px-10 sm:px-20 lg:px-40 text-center">
        <h2
          class="text-white dark:text-zinc-300 font-semibold leading-tight text-2xl sm:text-3xl md:text-4xl my-5"
        >
          Não Perca a Sua Chance!
        </h2>
        <p class="text-white text-xl sm:text-2xl dark:text-zinc-300">
          Estamos preparando um curso completo e exclusivo para te guiar nessa jornada de
          crescimento. Seja um dos primeiros a receber uma oferta especial de lançamento!
        </p>

        <div
          id="lista-de-espera"
          class="flex-inline border-2 shadow-xl rounded-lg sm:w-10/12 md:w-auto xl:w-9/12 2xl:w-8/12 mx-auto mt-10"
        >
          <h3
            class="text-white dark:text-zinc-300 font-semibold leading-tight text-xl sm:text-2xl md:text-3xl my-5"
          >
            Entre para a Lista de Espera
          </h3>
          <Form class="w-full mx-auto" :initial-values="formValues" @submit="addLead">
            <div class="md:flex md:items-center">
              <div class="px-4 w-auto md:w-1/3">
                <label
                  class="block text-white font-bold text-left md:text-right mb-1 md:mb-0 pr-4 text-md sm:text-lg"
                  for="inline-full-name"
                >
                  Nome
                </label>
              </div>
              <div class="px-4 w-auto md:w-2/3">
                <Field
                  id="inline-full-name"
                  name="name"
                  class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                  type="text"
                  rules="required|name"
                  placeholder="João da Silva"
                />
              </div>
            </div>
            <div class="md:flex md:items-center text-left mb-6">
              <div class="px-4 w-auto md:w-1/3"></div>
              <div class="px-4 w-auto md:w-2/3">
                <ErrorMessage name="name" class="text-[#bb2738] text-sm" />
              </div>
            </div>
            <div class="md:flex md:items-center">
              <div class="px-4 w-auto md:w-1/3">
                <label
                  class="block text-white font-bold text-left md:text-right mb-1 md:mb-0 pr-4 text-md sm:text-lg"
                  for="inline-telefone"
                >
                  Celular
                </label>
              </div>
              <div class="px-4 w-auto md:w-2/3">
                <Field
                  id="inline-telefone"
                  name="phone"
                  class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                  type="text"
                  rules="required|phone"
                  placeholder="(11)99999-9999"
                />
              </div>
            </div>
            <div class="md:flex md:items-center text-left mb-6">
              <div class="px-4 w-auto md:w-1/3"></div>
              <div class="px-4 w-auto md:w-2/3">
                <ErrorMessage name="phone" class="text-[#bb2738] text-sm" />
              </div>
            </div>
            <div class="md:flex md:items-center">
              <div class="px-4 w-auto md:w-1/3">
                <label
                  class="block text-white font-bold text-left md:text-right mb-1 md:mb-0 pr-4 text-md sm:text-lg"
                  for="inline-email"
                >
                  E-mail
                </label>
              </div>
              <div class="px-4 w-auto md:w-2/3">
                <Field
                  id="inline-email"
                  name="email"
                  class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                  type="email"
                  rules="required|email"
                  placeholder="joao_meu_melhor_email@gmail.com"
                />
              </div>
            </div>
            <div class="md:flex md:items-center text-left mb-6">
              <div class="px-4 w-auto md:w-1/3"></div>
              <div class="px-4 w-auto md:w-2/3">
                <ErrorMessage name="email" class="text-[#bb2738] text-sm" />
              </div>
            </div>
            <div class="md:flex md:items-center mb-6">
              <div class="md:w-1/3"></div>
              <label class="md:w-2/3 block text-white font-bold">
                <Field
                  name="receberNotificacoes"
                  class="mr-2 leading-tight"
                  type="checkbox"
                  :unchecked-value="false"
                  :value="true"
                />
                <span class="text-md">Receba nossas comunicações por e-mail! </span>
              </label>
            </div>
            <div class="md:flex md:items-center mb-6">
              <div class="md:w-1/3"></div>
              <div class="md:w-2/3 block text-white font-bold">
                <NuxtTurnstile ref="turnstile" v-model="token" />
              </div>
            </div>
            <div class="md:flex md:items-center mb-10">
              <div class="md:w-1/3"></div>
              <div class="md:w-2/3">
                <button
                  class="bg-[#FFD13C] hover:bg-[#c09e2d] focus:shadow-outline focus:outline-none text-black text-md font-bold p-6 rounded shadow-lg"
                  type="submit"
                >
                  Venha ser um Codernista!
                </button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// Initial values
const formValues = {
  name: '',
  phone: '',
  email: '',
  receberNotificacoes: false,
}

const turnstile = ref()

export default {
  data: () => ({
    token: '',
  }),
  methods: {
    resetTurnstile() {
      turnstile.value?.reset()
    },
    async addLead(values, actions) {
      try {
        if (!this.token) {
          window.alert('Por favor, complete o CAPTCHA.')
          return
        }

        const response = await $fetch<ApiResponse>(String(this.$config.public.apiUrl), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Request-Method': 'POST',
            'Access-Control-Request-Headers': 'content-type',
            Origin: 'https://movimento.codernista.com.br',
          },
          body: JSON.stringify({
            name: values.name,
            phone: values.phone,
            email: values.email,
            receberNotificacoes: values.receberNotificacoes,
            token: this.token,
          }),
        })

        if (response.statusCode === 200) {
          // Reset the form fields
          actions.setValues({
            name: '',
            phone: '',
            email: '',
            receberNotificacoes: false,
          })
          window.location.reload()
          window.alert('Registro salvo com sucesso!')
        } else if (response.statusCode === 400) {
          // Handle errors
          window.alert(response.body.message + '\n\nErro:' + response.body.error)
        } else {
          // Handle errors
          window.alert(response.body.message)
        }
      } catch (error: unknown) {
        // Handle network error
        if (typeof error === 'string') {
          console.error('Network error:', error.toUpperCase())
        } else if (error instanceof Error) {
          console.error('Network error:', error.message)
        }
      } finally {
        // Reset the token
        this.resetTurnstile()
      }
    },
  },
}
</script>
